/**
 * Serialization between Tiptap document and ContentBlocks format (backend).
 * ContentBlocks: [{ sortOrder, type, value }] where type is 'text' | 'image' | 'audio' | 'link'
 */

/**
 * Convert Tiptap JSON document to ContentBlocks array for API.
 * @param {Object} doc - Tiptap JSON document (doc.content array)
 * @returns {Array<{ sortOrder: number, type: string, value: string }>}
 */
export function tiptapToContentBlocks(doc) {
  if (!doc?.content) return [{ sortOrder: 0, type: 'text', value: '' }];

  const blocks = [];
  let textBuffer = [];
  let sortOrder = 0;

  function flushTextBuffer() {
    if (textBuffer.length === 0) return;
    const html = textBuffer.map((n) => nodeToHtml(n)).join('');
    if (html.trim()) {
      blocks.push({ sortOrder: sortOrder++, type: 'text', value: html });
    }
    textBuffer = [];
  }

  function nodeToHtml(node) {
    if (!node) return '';
    if (node.type === 'text') {
      let text = node.text || '';
      if (node.marks?.length) {
        node.marks.forEach((mark) => {
          if (mark.type === 'bold') text = `<strong>${text}</strong>`;
          else if (mark.type === 'italic') text = `<em>${text}</em>`;
          else if (mark.type === 'underline') text = `<u>${text}</u>`;
          else if (mark.type === 'strike') text = `<s>${text}</s>`;
          else if (mark.type === 'link') text = `<a href="${mark.attrs?.href || '#'}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        });
      }
      return text;
    }
    if (node.type === 'paragraph') {
      const inner = (node.content || []).map(nodeToHtml).join('');
      return `<p>${inner || '<br>'}</p>`;
    }
    if (node.type === 'heading') {
      const level = node.attrs?.level || 1;
      const inner = (node.content || []).map(nodeToHtml).join('');
      return `<h${level}>${inner}</h${level}>`;
    }
    if (node.type === 'bulletList') {
      const items = (node.content || []).map((li) => {
        const inner = (li.content || []).map(nodeToHtml).join('');
        return `<li>${inner}</li>`;
      }).join('');
      return `<ul>${items}</ul>`;
    }
    if (node.type === 'orderedList') {
      const items = (node.content || []).map((li) => {
        const inner = (li.content || []).map(nodeToHtml).join('');
        return `<li>${inner}</li>`;
      }).join('');
      return `<ol>${items}</ol>`;
    }
    if (node.type === 'listItem') {
      const inner = (node.content || []).map(nodeToHtml).join('');
      return `<li>${inner}</li>`;
    }
    if (node.type === 'blockquote') {
      const inner = (node.content || []).map(nodeToHtml).join('');
      return `<blockquote>${inner}</blockquote>`;
    }
    if (node.type === 'hardBreak') return '<br>';
    return '';
  }

  function walk(nodes) {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'image') {
        flushTextBuffer();
        const value = node.attrs?.src || '';
        if (value) {
          const align = node.attrs?.align || 'center';
          const caption = node.attrs?.caption || null;
          const width = node.attrs?.width || null;
          const height = node.attrs?.height || null;
          const rotate = node.attrs?.rotate ?? 0;
          blocks.push({ sortOrder: sortOrder++, type: 'image', value: JSON.stringify({ url: value, align, caption, width, height, rotate }) });
        }
      } else if (node.type === 'audio') {
        flushTextBuffer();
        const value = node.attrs?.src || '';
        if (value) {
          blocks.push({ sortOrder: sortOrder++, type: 'audio', value });
        }
      } else if (node.type === 'linkBlock') {
        flushTextBuffer();
        const href = node.attrs?.href || '';
        const text = node.attrs?.text || href;
        if (href) {
          blocks.push({ sortOrder: sortOrder++, type: 'link', value: JSON.stringify({ url: href, text }) });
        }
      } else if (node.type === 'paragraph' || node.type === 'heading' || node.type === 'bulletList' || node.type === 'orderedList' || node.type === 'blockquote') {
        textBuffer.push(node);
      } else if (node.content) {
        walk(node.content);
      }
    }
  }

  walk(doc.content);
  flushTextBuffer();

  if (blocks.length === 0) {
    blocks.push({ sortOrder: 0, type: 'text', value: '' });
  }
  return blocks;
}

/**
 * Convert ContentBlocks to Tiptap-compatible JSON content.
 * @param {Array<{ type: string, value: string }>} blocks - ContentBlocks from API
 * @returns {Object} Tiptap doc content
 */
export function contentBlocksToTiptap(blocks) {
  if (!blocks?.length) return { type: 'doc', content: [{ type: 'paragraph' }] };

  const content = [];

  for (const block of blocks) {
    const type = (block.type || 'text').toLowerCase();
    const value = block.value || '';

    if (type === 'text') {
      if (!value.trim()) {
        content.push({ type: 'paragraph' });
        continue;
      }
      const parsed = parseHtmlToNodes(value);
      content.push(...parsed);
    } else if (type === 'image') {
      try {
        const parsed = JSON.parse(value);
        const url = parsed?.url || value;
        content.push({
          type: 'image',
          attrs: {
            src: url,
            align: parsed?.align || 'center',
            caption: parsed?.caption || null,
            width: parsed?.width || null,
            height: parsed?.height || null,
            rotate: parsed?.rotate ?? 0,
          },
        });
      } catch {
        content.push({ type: 'image', attrs: { src: value, align: 'center', caption: null, width: null, height: null, rotate: 0 } });
      }
      content.push({ type: 'paragraph' });
    } else if (type === 'audio') {
      content.push({ type: 'audio', attrs: { src: value } });
      content.push({ type: 'paragraph' });
    } else if (type === 'link') {
      try {
        const parsed = JSON.parse(value);
        const href = parsed?.url || value;
        const text = parsed?.text || href;
        content.push({ type: 'linkBlock', attrs: { href, text } });
      } catch {
        content.push({ type: 'linkBlock', attrs: { href: value, text: value } });
      }
      content.push({ type: 'paragraph' });
    }
  }

  if (content.length === 0) content.push({ type: 'paragraph' });
  return { type: 'doc', content };
}

/**
 * Simple HTML to Tiptap nodes. Handles p, h1-h3, ul, ol, li, blockquote, strong, em, u, s, a, br.
 */
function parseHtmlToNodes(html) {
  if (!html?.trim()) return [{ type: 'paragraph' }];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const nodes = [];

  function walk(el) {
    if (!el) return;

    if (el.nodeType === Node.TEXT_NODE) {
      const text = el.textContent || '';
      if (text) return { type: 'text', text };
      return null;
    }

    if (el.nodeType !== Node.ELEMENT_NODE) return null;

    const tag = el.tagName?.toLowerCase();
    const children = Array.from(el.childNodes).map(walk).filter(Boolean);

    if (tag === 'p') {
      return { type: 'paragraph', content: children.length ? children : undefined };
    }
    if (tag === 'h1') return { type: 'heading', attrs: { level: 1 }, content: children };
    if (tag === 'h2') return { type: 'heading', attrs: { level: 2 }, content: children };
    if (tag === 'h3') return { type: 'heading', attrs: { level: 3 }, content: children };
    if (tag === 'ul') return { type: 'bulletList', content: children };

    if (tag === 'ol') return { type: 'orderedList', content: children };
    if (tag === 'li') return { type: 'listItem', content: children.length ? children : [{ type: 'paragraph' }] };
    if (tag === 'blockquote') return { type: 'blockquote', content: children };

    if (tag === 'br') return { type: 'hardBreak' };

    function extractText(n) {
      if (!n) return '';
      if (n.text) return n.text;
      if (n.content) return n.content.map(extractText).join('');
      return '';
    }

    if (tag === 'strong' || tag === 'b') {
      const text = children.map(extractText).join('');
      return text ? { type: 'text', marks: [{ type: 'bold' }], text } : null;
    }
    if (tag === 'em' || tag === 'i') {
      const text = children.map(extractText).join('');
      return text ? { type: 'text', marks: [{ type: 'italic' }], text } : null;
    }
    if (tag === 'u') {
      const text = children.map(extractText).join('');
      return text ? { type: 'text', marks: [{ type: 'underline' }], text } : null;
    }
    if (tag === 's' || tag === 'strike') {
      const text = children.map(extractText).join('');
      return text ? { type: 'text', marks: [{ type: 'strike' }], text } : null;
    }
    if (tag === 'a') {
      const href = el.getAttribute('href') || '#';
      const text = el.textContent || href;
      return { type: 'text', marks: [{ type: 'link', attrs: { href } }], text };
    }

    return children.length ? children[0] : null;
  }

  const body = doc.body;
  const blocks = Array.from(body.childNodes).map(walk).filter(Boolean);

  for (const b of blocks) {
    if (b.type === 'paragraph' || b.type === 'heading' || b.type === 'bulletList' || b.type === 'orderedList' || b.type === 'blockquote') {
      nodes.push(b);
    } else {
      nodes.push({ type: 'paragraph', content: [b] });
    }
  }

  if (nodes.length === 0) nodes.push({ type: 'paragraph' });
  return nodes;
}
